import type { Course } from './courses';

/**
 * Lazy loader for the courses bundle.
 *
 * Rationale: the `COURSES` array is ~1.7 MB / 516 KB gzipped (chunk-courses-data
 * in the build output). Statically importing it from the eager-loaded
 * courseStore + activity-id-resolver pulled the entire chunk into the
 * initial-paint critical path. This loader exposes:
 *
 *   - `prefetchCourses()` — triggers the dynamic import; resolves with the
 *     COURSES array. Idempotent + memoised (subsequent calls return the same
 *     promise / resolved array). Pages that need courses (CoursesPage,
 *     CourseProgress) call this on mount so the data is hot by the time the
 *     UI scrolls to a course-using component.
 *
 *   - `getCoursesIfLoaded()` — synchronous accessor used by callers that
 *     can't go async (the activity-id-resolver renders inside the parent
 *     ActivityFeed; the resolver returns "Course Lesson" if courses aren't
 *     loaded yet, then the next render lights up with the real title once
 *     prefetch resolves).
 *
 * Net result: chunk-courses-data drops out of the entry-chunk dependency
 * graph and only loads when prefetchCourses is invoked, which only happens
 * when a kid (or the parent dashboard) reaches a courses-related surface.
 */

let _cache: Course[] | null = null;
let _promise: Promise<Course[]> | null = null;

/** Trigger the lazy import (or return the resolved promise if already loaded). */
export function prefetchCourses(): Promise<Course[]> {
  if (_cache) return Promise.resolve(_cache);
  if (!_promise) {
    _promise = import('./courses').then((m) => {
      _cache = m.COURSES;
      return m.COURSES;
    });
  }
  return _promise;
}

/** Returns the cached array if loaded, or null if not yet hydrated. */
export function getCoursesIfLoaded(): Course[] | null {
  return _cache;
}

/** Test-only: reset module-level cache. Don't call from production code. */
export function __resetCoursesCacheForTests(): void {
  _cache = null;
  _promise = null;
}
